import { z } from 'zod'
import { calendarDate } from './dates'
import { itemSourceSchema } from './item-source'
import { estimateMinutesSchema, headsUpSchema, itemPlanSchema } from './plan'

export { type ItemSource, itemSourceSchema, itemSources } from './item-source'

/** What an item is to the user. `null` while it still sits untriaged in the inbox. */
export const itemKinds = ['task', 'owe', 'waiting', 'fyi'] as const
export const itemKindSchema = z.enum(itemKinds)
export type ItemKind = z.output<typeof itemKindSchema>

export const itemStatuses = ['inbox', 'open', 'snoozed', 'done', 'dropped'] as const
export const itemStatusSchema = z.enum(itemStatuses)
export type ItemStatus = z.output<typeof itemStatusSchema>

/** Statuses an item returns to after a snooze or a reopen. */
export const activeStatusSchema = z.enum(['inbox', 'open'])
export type ActiveStatus = z.output<typeof activeStatusSchema>

export const itemTypes = [
  'review',
  'reply',
  'deliverable',
  'decision',
  'meeting',
  'admin',
  'fix',
  'other',
] as const
export const itemTypeSchema = z.enum(itemTypes)
export type ItemType = z.output<typeof itemTypeSchema>

/** Where the work came from: self-initiated, asked by someone, created by the agent, imported. */
export const itemOrigins = ['own', 'request', 'agent', 'import'] as const
export const itemOriginSchema = z.enum(itemOrigins)
export type ItemOrigin = z.output<typeof itemOriginSchema>

/** Which surface created or changed something (for insights and the audit log). */
export const viaValues = [
  'ui',
  'palette',
  'voice',
  'agent',
  'mcp',
  'connector',
  'rule',
  'scenario',
  'system',
] as const
export const viaSchema = z.enum(viaValues)
export type Via = z.output<typeof viaSchema>

export const closedReasons = ['done', 'dropped', 'ignored', 'acknowledged'] as const
export const closedReasonSchema = z.enum(closedReasons)
export type ClosedReason = z.output<typeof closedReasonSchema>

/**
 * How much an item matters compared with the rest. `normal` unless the user (or hugo's
 * suggestion they accepted) said otherwise. The planner orders work with room to spare by it;
 * it never makes a dated item late (see packages/insights).
 */
export const itemPriorities = ['high', 'normal', 'low'] as const
export const itemPrioritySchema = z.enum(itemPriorities)
export type ItemPriority = z.output<typeof itemPrioritySchema>

export const itemSizes = ['S', 'M', 'L'] as const
export const itemSizeSchema = z.enum(itemSizes)
export type ItemSize = z.output<typeof itemSizeSchema>

/** Inbox triage choices (keyboard: t, o, w, f, x). */
export const triageChoices = ['task', 'owe', 'waiting', 'fyi', 'ignore'] as const
export const triageChoiceSchema = z.enum(triageChoices)
export type TriageChoice = z.output<typeof triageChoiceSchema>

/** The lists the UI shows. */
/**
 * Views of the user's items. `open`: everything they are responsible for in one list (open
 * tasks, promises and waits, by due date); `tasks`, `owe` and `waiting` are its parts.
 */
export const itemViews = ['inbox', 'open', 'tasks', 'owe', 'waiting', 'snoozed', 'done'] as const
export const itemViewSchema = z.enum(itemViews)
export type ItemView = z.output<typeof itemViewSchema>

export const snoozePresets = ['later_today', 'tomorrow', 'in_2_days', 'next_week'] as const
export const snoozePresetSchema = z.enum(snoozePresets)
export type SnoozePreset = z.output<typeof snoozePresetSchema>

export const personSchema = z.object({ id: z.uuid(), name: z.string() })
export type Person = z.output<typeof personSchema>

/** How sure hugo's AI is about a suggestion. */
export const suggestionConfidences = ['high', 'medium', 'low'] as const
export const suggestionConfidenceSchema = z.enum(suggestionConfidences)
export type SuggestionConfidence = z.output<typeof suggestionConfidenceSchema>

export const SUGGESTION_REASON_MAX = 140

/**
 * hugo's AI suggestion for an inbox item: where it belongs, the kind of work, the date the ask
 * states and one sentence why. Items carry it only while they are in the inbox and the
 * suggestion is neither applied nor dismissed.
 */
export const itemSuggestionSchema = z.object({
  choice: triageChoiceSchema,
  type: itemTypeSchema.nullable(),
  /** The date the ask states, YYYY-MM-DD in the user's time zone; null when it states none. */
  dueDate: z.iso.date().nullable(),
  confidence: suggestionConfidenceSchema,
  /** How urgent the sender makes it: high or low is applied (as the user's) when filed. */
  priority: itemPrioritySchema.default('normal'),
  reason: z.string().max(SUGGESTION_REASON_MAX),
  createdAt: z.iso.datetime(),
})
export type ItemSuggestion = z.output<typeof itemSuggestionSchema>

/**
 * Where the user put an item on the board: being worked on now, planned for a day, planned for a
 * week, or in the backlog. On an item, null means hugo places it by its plan (automatic).
 */
export const boardLanes = ['now', 'day', 'week', 'backlog'] as const
export const boardLaneSchema = z.enum(boardLanes)
export type BoardLane = z.output<typeof boardLaneSchema>

/** Who filed an item out of the inbox: the user, or hugo's agent (auto-filing, undoable). */
export const filedByValues = ['user', 'agent'] as const
export const filedBySchema = z.enum(filedByValues)
export type FiledBy = z.output<typeof filedBySchema>

export const itemSchema = z.object({
  id: z.uuid(),
  title: z.string(),
  notes: z.string().nullable(),
  kind: itemKindSchema.nullable(),
  status: itemStatusSchema,
  type: itemTypeSchema.nullable(),
  size: itemSizeSchema.nullable(),
  tags: z.array(z.string()),
  /**
   * The priority hugo plans with: the user's, else the first of their priority rules that matches,
   * else normal (items.list, item.get, the board and Today; elsewhere what the user set).
   */
  priority: itemPrioritySchema.default('normal'),
  /** Whether the user set the priority themselves (false: their rules decide). */
  prioritySet: z.boolean().default(false),
  /** Which of the user's priority rules set it (its place in their list), when one did. */
  priorityRuleIndex: z.number().int().min(0).nullable().default(null),
  origin: itemOriginSchema,
  source: itemSourceSchema,
  counterpart: personSchema.nullable(),
  dueAt: z.iso.datetime().nullable(),
  dueAllDay: z.boolean(),
  snoozedUntil: z.iso.datetime().nullable(),
  requestedAt: z.iso.datetime().nullable(),
  createdAt: z.iso.datetime(),
  completedAt: z.iso.datetime().nullable(),
  closedReason: closedReasonSchema.nullable(),
  version: z.number().int(),
  /** hugo's open suggestion while the item waits in the inbox (see itemSuggestionSchema). */
  suggestion: itemSuggestionSchema.nullable().default(null),
  /** Who filed it out of the inbox; null while it was never filed (or the filing was undone). */
  filedBy: filedBySchema.nullable().default(null),
  /** Time needed in minutes, as the user estimated it; null: hugo assumes a default by type. */
  estimateMinutes: estimateMinutesSchema.nullable().default(null),
  /** Where the user put it on the board; null: hugo places it by its plan. */
  planLane: boardLaneSchema.nullable().default(null),
  /** The planned day (lane `day`), or the first day of the planned week (lane `week`). */
  planDate: calendarDate.nullable().default(null),
  /** Focus work: on its day it comes before the day's other work. */
  planFocus: z.boolean().default(false),
  /** Its place among the cards the user put in its board column, lower first. */
  planRank: z.number().nullable().default(null),
  /** When its work gets done (dated tasks and I owe items). Filled by items.list and item.get only. */
  plan: itemPlanSchema.nullable().default(null),
  /** Who should hear from the user about it now. Filled by items.list and item.get only. */
  headsUp: headsUpSchema.nullable().default(null),
  /**
   * Waiting on: when the person last wrote after the follow-up date was set (hugo then stops
   * suggesting reminders until the user sets a new date); null when nobody wrote since. Filled
   * by items.list and item.get only.
   */
  repliedAt: z.iso.datetime().nullable().default(null),
})
export type Item = z.output<typeof itemSchema>

export const itemHistoryEntrySchema = z.object({
  seq: z.number().int(),
  type: z.string(),
  actor: z.string(),
  data: z.record(z.string(), z.unknown()),
  at: z.iso.datetime(),
  undone: z.boolean(),
  /**
   * The change (action) this entry belongs to, so a user can undo it (e.g. hugo's filing) with
   * `action.undo`; null for entries outside a tool call.
   */
  actionId: z.uuid().nullable().optional(),
  /** Whether `action.undo` would undo this entry's change now (undoable tool, in time, not undone). */
  undoable: z.boolean().default(false),
})
export type ItemHistoryEntry = z.output<typeof itemHistoryEntrySchema>

export const TITLE_MAX = 500
export const NOTES_MAX = 10_000
export const PERSON_NAME_MAX = 120
export const TAG_MAX = 40
/** The most tags an item (or the list of personal tags) has. */
export const TAGS_MAX = 20

/**
 * Tags group items by area (family, house, a side project; decision 97). A tag is one word:
 * a letter first, then letters, digits, "-" and "_", lowercase, written with # in capture.
 */
export const TAG_PATTERN = /^\p{L}[\p{L}\p{N}_-]*$/u

/** "#Side project" → "side-project": no #, spaces as dashes, lowercase. */
export function normalizeTag(raw: string): string {
  return raw.trim().replace(/^#+/, '').trim().replace(/\s+/g, '-').toLowerCase()
}

export const tagSchema = z
  .string()
  .transform(normalizeTag)
  .pipe(
    z
      .string()
      .min(1, 'a tag needs a word')
      .max(TAG_MAX)
      .regex(TAG_PATTERN, 'a tag is one word: letters, digits, - and _'),
  )

/** Tags, normalized, each once, in the order given. */
export const tagsSchema = z
  .array(tagSchema)
  .transform((tags) => [...new Set(tags)])
  .pipe(z.array(z.string()).max(TAGS_MAX))
